import React, { createContext, useContext, useCallback, useState, useEffect, ReactNode } from "react";

export type HandleUser = {
  id?: string;
  googleId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  whoopId?: string | null;
  withingsUserId?: string | null;
};

type AuthContextType = {
  user: HandleUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  fetchProfile: () => Promise<void>;
  loginWithGoogle: () => void;
  loginWithWhoop: () => void;
  refreshWithWhoop: () => Promise<void>;
  loginWithWithings: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Production requests always stay on the current Handle origin, never an old HandleChef backend.\nconst BACKEND_URL = process.env.NODE_ENV==="production" ? "/api" : (process.env.REACT_APP_BACKEND_URL || "/api");

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<HandleUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(BACKEND_URL + "/auth/google/session", {
        method:"GET", credentials:"include", headers:{ "Accept":"application/json" },
      });
      if (!response.ok) throw new Error("Unauthenticated");
      const data = await response.json();
      if (!data.user) throw new Error("No profile returned");
      setUser(data.user);
      setIsAuthenticated(true);
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  },[]);

  useEffect(()=>{ void fetchProfile(); },[fetchProfile]);

  const loginWithGoogle = () => { window.location.href = BACKEND_URL + "/auth/google/login"; };
  const loginWithWhoop = () => { window.location.href = BACKEND_URL + "/auth/whoop/login"; };
  const loginWithWithings = () => { window.location.href = BACKEND_URL + "/auth/withings/login"; };
  const refreshWithWhoop = async (): Promise<void> => { window.location.href = BACKEND_URL + "/auth/whoop/login?refresh=true"; };
  const logout = () => {
    void (async()=>{
      try {
        const response=await fetch(BACKEND_URL + "/auth/logout",{method:"POST",credentials:"include"});
        if (!response.ok) throw new Error("Unable to sign out");
        setUser(null);
        setIsAuthenticated(false);
        window.location.href="/";
      } catch (error) {
        console.error("Logout failed",error);
      }
    })();
  };

  return <AuthContext.Provider value={{user,isAuthenticated,isLoading,fetchProfile,loginWithGoogle,loginWithWhoop,refreshWithWhoop,loginWithWithings,logout}}>
    {children}
  </AuthContext.Provider>;
};

export const useAuth = () => {
  const context=useContext(AuthContext);
  if(!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
